"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createSupabaseClient } from '@/lib/supabase';
import NinjaLogo from '@/components/NinjaLogo';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const router = useRouter();
  
  // Check if already logged in and redirect
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsInitializing(true);
        setAuthError(null);
        const supabase = createSupabaseClient();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        console.log("Login page init - Session check:", !!session);
        
        if (error) {
          console.error("Error in auth check:", error);
          setAuthError("Authentication service unavailable. Please try again.");
          setIsInitializing(false);
          return;
        }
        
        if (session) {
          console.log("User already logged in, redirecting to conversations");
          // Add a small delay before redirecting to ensure state is consistent
          setTimeout(() => {
            router.push('/conversations');
          }, 100);
        } else {
          // Explicitly set initializing to false when no session is found
          setIsInitializing(false);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setAuthError("Authentication service unavailable. Please try again.");
        setIsInitializing(false);
      } finally {
        // Set a timeout to ensure we don't get stuck in initializing state
        const timer = setTimeout(() => {
          if (isInitializing) {
            setIsInitializing(false);
            console.log("Forcing initialization to complete after timeout");
          }
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    };
    
    checkAuth();
    
    // Clean up any auth-related state on unmount
    return () => {
      // Don't reset isInitializing on unmount as it can cause issues with redirects
    };
  }, [router]); // Remove isInitializing from dependencies

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log("Attempting login with:", email);
      const supabase = createSupabaseClient();
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Login error:", error.message);
        throw error;
      }

      console.log("Login successful", data.session ? "Session exists" : "No session");
      
      // Ensure we have a valid session before redirecting
      if (!data.session) {
        throw new Error("No session returned after login");
      }
      
      // Wait briefly to ensure auth state is propagated
      setTimeout(() => {
        // Force a more complete refresh to ensure all components are aware of the auth state change
        console.log("Redirecting to conversations after successful login");
        router.push('/conversations');
        router.refresh();
      }, 100);
    } catch (err) {
      console.error("Login failed:", err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="text-center">
          <NinjaLogo size="2rem" className="mx-auto mb-4" />
          <p>Checking authentication status...</p>
        </div>
      </div>
    );
  }
  
  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="text-center max-w-md p-6 bg-card rounded-lg shadow">
          <NinjaLogo size="2rem" className="mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-4">Authentication Error</h2>
          <p className="text-destructive mb-4">{authError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center items-center gap-2 mb-2">
          <NinjaLogo size="2rem" />
          <h1 className="text-3xl font-bold text-center">DateHack</h1>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight">
          Log in to your account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-card px-6 py-12 shadow sm:rounded-lg sm:px-12">
          {error && (
            <div className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium leading-6">
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md border-0 bg-background py-1.5 shadow-sm ring-1 ring-inset ring-input placeholder:text-muted-foreground focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium leading-6">
                  Password
                </label>
                <div className="text-sm">
                  <Link href="/reset-password" className="font-semibold text-primary hover:text-primary/90">
                    Forgot password?
                  </Link>
                </div>
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-0 bg-background py-1.5 shadow-sm ring-1 ring-inset ring-input placeholder:text-muted-foreground focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {loading ? 'Logging in...' : 'Log in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <div className="mt-6 text-center text-sm">
              <p className="text-muted-foreground">
                Don't have an account?{' '}
                <Link href="/quiz" className="font-semibold text-primary hover:text-primary/90">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 